import React, { useEffect, useMemo, useState, useRef } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import api from "@/lib/api";
import { Check, Info } from "lucide-react";

/**
 * Phone-only field with automatic country detection.
 *
 * Iter 74b (Aug 2026) — the Country dropdown was removed at the Master
 * Admin's request. The component now derives the country + default wallet
 * currency purely from the E.164 phone number using libphonenumber-js and
 * calls `onCountryChange(detectedCode)` automatically. This keeps the same
 * public API — parent forms save with the detected country/currency without
 * any code change.
 *
 * Props:
 *   phone            (string)   current phone value
 *   countryCode      (string)   currently-stored ISO country code (kept in sync)
 *   onPhoneChange    (fn)       (newValue) => void
 *   onCountryChange  (fn)       (newCode)   => void  — invoked automatically on detection
 *   testIdPrefix     (string)   e.g. "create-agent"
 */
export default function CountryPhoneFields({
  phone,
  countryCode,
  onPhoneChange,
  onCountryChange,
  testIdPrefix = "country",
}) {
  const [countries, setCountries] = useState([]);
  const lastAppliedRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/countries")
      .then(({ data }) => { if (alive) setCountries(data.countries || []); })
      .catch(() => { if (alive) setCountries([]); });
    return () => { alive = false; };
  }, []);

  // Client-side country detection. Falls back to null if the phone can't
  // be parsed (e.g. still being typed).
  const detected = useMemo(() => {
    if (!phone) return null;
    try {
      const p = parsePhoneNumberFromString(phone);
      if (!p || !p.country) return null;
      return p.country; // ISO-3166 alpha-2
    } catch {
      return null;
    }
  }, [phone]);

  const detectedCountry = useMemo(
    () => countries.find((c) => c.code === detected) || null,
    [countries, detected],
  );

  // Auto-propagate the detected country to the parent form. We track the
  // last value we forwarded so we don't loop when the parent state updates
  // trigger a re-render.
  useEffect(() => {
    if (!onCountryChange) return;
    const target = detected || null;
    if (lastAppliedRef.current === target && countryCode === target) return;
    if (countryCode !== target) {
      lastAppliedRef.current = target;
      onCountryChange(target);
    }
  }, [detected, countryCode, onCountryChange]);

  return (
    <>
      <label className="text-xs text-slate-600 flex flex-col gap-1">
        <span>Phone number (E.164)</span>
        <input
          data-testid={`${testIdPrefix}-phone`}
          value={phone || ""}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="+56961000127"
          className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]/30 focus:border-[#0F62FE]"
        />
      </label>

      {/* Detection feedback — clear positive message once we recognize the country. */}
      {detected && detectedCountry && (
        <div
          className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 space-y-0.5"
          data-testid={`${testIdPrefix}-detected-country`}
        >
          <div className="flex items-center gap-1.5 font-semibold">
            <Check className="w-3.5 h-3.5" />
            Phone detected as: {detectedCountry.name}
          </div>
          <div className="pl-5 text-emerald-700">
            Default wallet on save:{" "}
            <b className="text-emerald-900">{detectedCountry.default_currency}</b>
          </div>
        </div>
      )}

      {/* Recognized by libphonenumber but not in our supported list — the
          save will still succeed with the ISO code, but we let the user know
          which currency policy applies (falls back to USD server-side). */}
      {detected && !detectedCountry && (
        <div
          className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 space-y-0.5"
          data-testid={`${testIdPrefix}-detected-unsupported`}
        >
          <div className="flex items-center gap-1.5 font-semibold">
            <Info className="w-3.5 h-3.5" />
            Phone detected as: {detected}
          </div>
          <div className="pl-5 text-blue-700">
            Default wallet on save: <b className="text-blue-900">USD</b>
          </div>
        </div>
      )}

      {/* Still typing — remind the user to include the country dial code. */}
      {phone && !detected && (
        <div
          className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600"
          data-testid={`${testIdPrefix}-detection-pending`}
        >
          Enter a full E.164 number (starting with <code>+</code> and country code)
          — the country and default wallet will be detected automatically.
        </div>
      )}
    </>
  );
}
